import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import * as ElementModels from '../../../models/report-view.models';
import { Chart } from 'angular-highcharts';
import { ChartService } from '../../chart/chart.service';
import { map, of, takeWhile } from 'rxjs';
import { getChartColors } from '../panel-utils';
import { SeriesOptionsType } from 'highcharts';
import { scatterPlotPlaceholder } from '../../../constants/highcharts-placeholders';
import { ElementBaseComponent } from '../ElementBaseComponent';
import { HelpTextService } from '../../../services/services-index';

@Component({
  selector: 'scatter-plot',
  templateUrl: './scatter-plot.component.html',
  styleUrls: ['../panel-elements.scss', './scatter-plot.component.scss'],
  standalone: false
})
export class ScatterPlotComponent extends ElementBaseComponent implements OnInit, OnDestroy {
  @Input() set dataSets(value: ElementModels.DataSet[]) {
    if (!!value) {
      this.processData(value[0]);
      this.isDataSetLoaded$.next(true);
    } else {
      this.isDataSetLoaded$.next(false);
    }
  }

  @Input() settings: ElementModels.ElementSettings;
  @Input() selectedMetrics: string[];

  currentChart: Chart;
  componentActive = true;

  placeholderChart: Chart;

  constructor(
    private chartService: ChartService,
    helpTextService: HelpTextService) {
    super(helpTextService);
  }
  ngOnInit(): void {
    this.initializePlaceholder();
  }

  ngOnDestroy(): void {
    this.componentActive = false;
  }

  private processData(dataSet: ElementModels.DataSet): void {
    const dimensionNames = this.getDimensionNames();

    if (!dimensionNames.length) {
      throw new Error('Dimension is required.');
    }

    const dimensionName = dimensionNames[0];
    const labelDimensionName = dimensionNames.length > 1 ? dimensionNames[1] : undefined;

    if (this.selectedMetrics.length !== 2) {
      throw new Error('Two metrics are required.');
    }

    const dimIndex = dataSet.columns.findIndex(col => col.name === dimensionName);
    const labelDimIndex = labelDimensionName
      ? dataSet.columns.findIndex(col => col.name === labelDimensionName)
      : -1;
    const metricIndices = this.selectedMetrics.map(metric =>
      dataSet.columns.findIndex(col => col.name === metric));

    if (dimIndex === -1 || metricIndices.some(index => index === -1)) {
      throw new Error('Invalid dimension or metric selection.');
    }

    // const seriesData = dataSet.rows.map(row => {
    //   if (!row[dimIndex].value || row[dimIndex].label === ''
    //       || this.settings.dimensionRowFilters?.some(f => f === row[dimIndex].value || f === row[dimIndex].label)) {
    //     return;
    //   }

    //   return {
    //     x: row[metricIndices[0]].value,
    //     y: row[metricIndices[1]].value,
    //     xLabel: this.getMetricLabel(this.selectedMetrics[0]),
    //     yLabel: this.getMetricLabel(this.selectedMetrics[1]),
    //     xValueLabel: row[metricIndices[0]].label || row[metricIndices[0]].value,
    //     yValueLabel: row[metricIndices[1]].label || row[metricIndices[1]].value,
    //     name: row[dimIndex].label || row[dimIndex].value
    //   };
    // });

    this.currentChart = this.generateChart(dataSet.rows, dataSet, dimIndex, labelDimIndex, metricIndices);

    this.chartService.reflowChart$.pipe(
      takeWhile(() => this.componentActive),
      map(() => {
        setTimeout(() => {
          this.currentChart.ref.reflow();
        }, 400);
      })
    ).subscribe()
  }

  private generateChart(
    rows: ElementModels.RowValue[][],
    dataSet: ElementModels.DataSet,
    dimIndex: number,
    labelDimIndex: number,
    metricIndices: number[]
  ): Chart {
    const xAxisIsPercentage = this.isPercentageMetric(this.selectedMetrics[0], dataSet);
    const yAxisIsPercentage = this.isPercentageMetric(this.selectedMetrics[1], dataSet);

    // Create a series for each data point
    const series = rows.map(row => {
      const codeCell = row[dimIndex];
      const labelCell = labelDimIndex >= 0 ? row[labelDimIndex] : undefined;

      // Skip rows not matching the criteria (similar to previous filtering logic)
      if (!codeCell?.value || codeCell.label === ''
        || this.settings.dimensionRowFilters?.some(f => f === codeCell.value || f === codeCell.label)) {
        return null; // Return null for rows that should be skipped
      }

      const pointKey = this.getCellDisplayValue(codeCell);
      const pointLabel = labelCell ? this.getCellDisplayValue(labelCell) : pointKey;
      const pointTooltipLabel = this.buildPointTooltipLabel(pointLabel, pointKey);

      return <SeriesOptionsType>{
        name: pointKey,
        type: 'scatter',
        data: [{
          x: row[metricIndices[0]].value,
          y: row[metricIndices[1]].value,
          custom: {
            xLabel: this.getMetricLabel(this.selectedMetrics[0], dataSet),
            yLabel: this.getMetricLabel(this.selectedMetrics[1], dataSet),
            xValueLabel: row[metricIndices[0]].label || row[metricIndices[0]].value,
            yValueLabel: row[metricIndices[1]].label || row[metricIndices[1]].value,
            tooltipLabel: pointTooltipLabel
          },
          name: pointKey // Name used for tooltip legend
        }],
        marker: {
          symbol: 'circle',
          radius: 6 // Customize marker size as needed
        }
      };
    }).filter(series => series !== null); // Filter out nulls from skipped rows

    const colors = getChartColors(this.settings);

    var chart = new Chart({
      chart: {
        type: 'scatter',
        zoomType: 'xy'
      },
      credits: { enabled: false },
      colors: colors,
      title: {
        text: this.getChartLabel(dataSet)
      },
      xAxis: {
        title: {
          text: this.getMetricLabel(this.selectedMetrics[0], dataSet)
        },
        labels: {
          formatter: function () {
            return xAxisIsPercentage ? (this.value * 100).toFixed(0) + '%' : this.value.toString();
          }
        }
      },
      yAxis: {
        title: {
          text: this.getMetricLabel(this.selectedMetrics[1], dataSet)
        },
        labels: {
          formatter: function () {
            return yAxisIsPercentage ? (this.value * 100).toFixed(0) + '%' : this.value.toString();
          }
        }
      },
      tooltip: {
        formatter: function () {
          const point = <any>this.point;
          const header = point.custom?.tooltipLabel || point.name;
          return '<b>' + header + '</b><br>' +
            `${point.custom.xLabel}: <b>` + point.custom.xValueLabel + '</b><br>' +
            `${point.custom.yLabel}: <b>` + point.custom.yValueLabel + '</b>';
        }
      },
      series: series
    });

    return chart;
  }

  private getCellDisplayValue(cell: ElementModels.RowValue | undefined): string {
    if (!cell) {
      return '';
    }

    if (cell.label !== undefined && cell.label !== null && cell.label !== '') {
      return String(cell.label);
    }

    if (cell.value !== undefined && cell.value !== null) {
      return String(cell.value);
    }

    return '';
  }

  private buildPointTooltipLabel(name: string, code: string): string {
    if (name && code && name !== code) {
      return `${name} (${code})`;
    }

    return name || code || '';
  }

  private getDimensionLabel(dim: string, dataSet: ElementModels.DataSet): string {
    const dimIndex = dataSet.columns.findIndex(col => col.name === dim);
    return dataSet.columns[dimIndex].displayName || dataSet.columns[dimIndex].name;
  }

  private getMetricLabel(metric: string, dataSet: ElementModels.DataSet): string {
    const metricIndex = dataSet.columns.findIndex(col => col.name === metric);
    return dataSet.columns[metricIndex].displayName || dataSet.columns[metricIndex].name;
  }

  private getChartLabel(dataSet: ElementModels.DataSet): string {
    const metricLabels = this.selectedMetrics.map(metric => this.getMetricLabel(metric, dataSet));

    return metricLabels.join(' vs ');
  }

  private isPercentageMetric(metricName: string, dataSet: ElementModels.DataSet): boolean {
    // Implement logic to determine if the metric is a percentage
    // This could be based on the metric name, or any other criteria you have
    const metricIndex = dataSet.columns.findIndex(col => col.name === metricName);
    const metric = dataSet.columns[metricIndex];
    return dataSet.rows[0][metricIndex].label?.includes('%') || metric.name?.includes('%');
  }

  private initializePlaceholder(): void {
    if (this.placeholderChart) {
      // exit if chart is already initialized
      return;
    }

    // without setTimeout the placeholder chart
    // won't respect the size of the parent container
    setTimeout(() => {
      this.placeholderChart = new Chart(scatterPlotPlaceholder);
    }, 0);
  }
}